import { Button, Center, Group, Modal, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconRefresh, IconTransform } from '@tabler/icons-react';
import { useState } from 'react';
import useFetch from '../../helpers/useFetch';
import useIsSuperAdmin from '../../helpers/useIsSuperAdmin';
import ClickTrackingHostHealth from '../ClickTrackingHostHealth';
import DnsRecordsTable, { DnsRecordRow } from '../DnsRecordsTable';
import SenderDomainMailHealth from '../SenderDomainMailHealth';
import SuperAdminBadge from '../SuperAdmin/SuperAdminBadge';
import ConfirmationModal from './ConfirmationModal';

export default function DomainEmailVerificationModal({
  currentAccount,
  currentDomain,
  verificationStatus,
  getVerificationStatusHuman,
  refreshVerificationStatus,
  show,
  close,
}) {
  const fetch = useFetch();
  const [recheckLoading, setRecheckLoading] = useState(false);
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [migrateConfirmOpen, setMigrateConfirmOpen] = useState(false);
  const isSuperAdmin = useIsSuperAdmin();

  if (!verificationStatus) {
    return '';
  }

  const recheckVerification = () => {
    setRecheckLoading(true);
    refreshVerificationStatus(() => setRecheckLoading(false));
  };

  const migrateToCname = () => {
    setMigrateLoading(true);
    setMigrateConfirmOpen(false);
    fetch(
      `/api/admin/accounts/${currentAccount.id}/domains/${currentDomain.id}/verificationStatus/migrateDkimToCname`,
      { method: 'POST' }
    )
      .then(() => {
        refreshVerificationStatus(() => {
          setMigrateLoading(false);
          notifications.show({
            title: 'Migration successful',
            message: 'DKIM record type has been migrated to CNAME.',
            color: 'green',
          });
        });
      })
      .catch((err) => {
        console.error(err);
        setMigrateLoading(false);
        notifications.show({
          title: 'Migration failed',
          message: 'There was an error migrating the DKIM record type. Please try again.',
          color: 'red',
        });
      });
  };

  const dkimType = verificationStatus.releva?.dkim?.type || 'TXT';

  return (
    <Modal size="full" opened={show} onClose={close} title="Email Sender Verification">
      <div style={{ paddingBottom: '1.5rem' }}>
        <Title order={4}>
          Required DNS Records ({getVerificationStatusHuman(verificationStatus.isEmailSendingVerified)})
        </Title>
        {verificationStatus.isEmailSendingVerified ? null : (
          <Text ta="center" c="dimmed" mb="md">
            Please add both DNS records below to enable email sending from {verificationStatus.emailSenderDomain}.
          </Text>
        )}
        <DnsRecordsTable>
          <DnsRecordRow
            record={verificationStatus.releva?.dkim ?? {}}
            idPrefix="relevaDkim"
            dataTour="emailVerificationDKIMRecords"
            defaultType="TXT"
          />
          <DnsRecordRow
            record={verificationStatus.releva?.resources ?? {}}
            idPrefix="relevaResources"
            dataTour="emailVerificationMainRecord"
            defaultType="CNAME"
          />
        </DnsRecordsTable>
        {/* Mantine unmounts modal children while it is closed, so the probe runs when the
            merchant opens this - which is where they land from the sending alert. Gated on
            emailSenderDomain like the settings mount site: without a sender domain there is
            no CNAME to derive, so the table above already reads "No verification token found"
            and probing (or telling them to publish a record that isn't shown) would contradict it. */}
        {verificationStatus.emailSenderDomain && (
          <ClickTrackingHostHealth accountId={currentAccount.id} domainId={currentDomain.id} />
        )}
        {/* Advisory, not a third required record: two of its verdicts ask for a record to
            be removed or published elsewhere, which would contradict the "add both records
            below" table above. Renders nothing until the API reports the check. */}
        <SenderDomainMailHealth
          check={verificationStatus.releva?.senderDomainMail}
          senderDomain={verificationStatus.emailSenderDomain}
        />
        {isSuperAdmin && dkimType === 'TXT' && (
          <Center py="sm">
            <Group gap="xs">
              <Button
                variant="light"
                size="sm"
                disabled={migrateLoading}
                onClick={() => setMigrateConfirmOpen(true)}
                leftSection={<IconTransform size={16} />}
              >
                {migrateLoading ? 'Migrating...' : 'Migrate DKIM to CNAME'}
              </Button>
              <SuperAdminBadge show tooltip="Only super admins can migrate DKIM records" />
            </Group>
          </Center>
        )}
        {dkimType === 'CNAME' &&
          verificationStatus.releva.dkim.txtValid !== undefined &&
          verificationStatus.releva.dkim.cnameValid !== undefined && (
            <Text ta="center" c="dimmed" size="sm" mt="xs">
              Migration status: TXT record {verificationStatus.releva.dkim.txtValid ? 'active' : 'not found'}
              {' | '}CNAME record {verificationStatus.releva.dkim.cnameValid ? 'active' : 'not found'}
            </Text>
          )}
        {verificationStatus.isEmailSendingVerified ? null : (
          <Stack align="center" py="xl">
            <Button
              size="lg"
              disabled={recheckLoading}
              onClick={recheckVerification}
              leftSection={<IconRefresh size={16} />}
            >
              {recheckLoading ? 'Checking...' : 'Re-check DNS Records'}
            </Button>
            <Text c="dimmed">
              Once you have added the DNS records, press the button above to re-check. DNS changes can take a few hours
              to propagate.
            </Text>
          </Stack>
        )}
      </div>
      <ConfirmationModal
        show={migrateConfirmOpen}
        close={() => setMigrateConfirmOpen(false)}
        confirm={migrateToCname}
        loading={migrateLoading}
        message="This will migrate DKIM verification from TXT to CNAME records. This action is irreversible. Are you sure you want to continue?"
      />
    </Modal>
  );
}
